import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ExpertRecord, Gender, UserRecord, UserRole } from '@libs/shared/api';
import { environment } from '@sprint-expert/environments/environment';

export interface ChannelConfig {
  pluginKey: string;
  memberId: string;
  profile: {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    birthday: string;
    mobileNumber: string;
    gender: Gender;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ChannelIoService {
  private previousChPluginDisplay: string | null = null;
  private chPluginObserver: MutationObserver | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public loadScript() {
    const w = window;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (w.ChannelIO) {
      return (window.console.error || window.console.log || function () {})(
        'ChannelIO script included twice.'
      );
    }
    const ch = function () {
      // eslint-disable-next-line prefer-rest-params
      ch.c(arguments);
    };
    ch.q = [];
    ch.c = function (args) {
      ch.q.push(args);
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    w.ChannelIO = ch;

    function l() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (w.ChannelIOInitialized) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      w.ChannelIOInitialized = true;
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
      s.charset = 'UTF-8';
      const x = document.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    }

    if (document.readyState === 'complete') {
      l();
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.attachEvent) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.attachEvent('onload', l);
      } else {
        window.addEventListener('DOMContentLoaded', l, false);
        window.addEventListener('load', l, false);
      }
    }
  }

  boot(user: UserRecord, expert: ExpertRecord) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.ChannelIO('boot', {
      pluginKey: environment.channelPluginKey,
      memberId: user.id,
      profile: {
        id: user.id,
        email: user.emailAddress,
        name: user.nickname,
        role: user.role,
        env: environment.name,
        gender: expert.gender,
      },
    } as ChannelConfig);
  }

  shutdown() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.ChannelIO('shutdown');
  }

  /**
   * Hide the ch-plugin element.
   * If the element isn’t in the DOM yet, a MutationObserver is created to hide it once added.
   */
  hideChPlugin(): void {
    const chPlugin = this.document.getElementById('ch-plugin');
    if (chPlugin) {
      this.previousChPluginDisplay = chPlugin.style.getPropertyValue('display');
      chPlugin.style.setProperty('display', 'none', 'important');
    } else {
      // If no observer exists, create one to detect when the element is added.
      if (!this.chPluginObserver) {
        this.chPluginObserver = new MutationObserver(mutations => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node instanceof HTMLElement && node.id === 'ch-plugin') {
                this.previousChPluginDisplay =
                  node.style.getPropertyValue('display');
                node.style.setProperty('display', 'none', 'important');
                // Once handled, disconnect the observer.
                if (this.chPluginObserver) {
                  this.chPluginObserver.disconnect();
                  this.chPluginObserver = null;
                }
                break;
              }
            }
          }
        });
        this.chPluginObserver.observe(this.document.body, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  /**
   * Show the ch-plugin element.
   * Also disconnects any active MutationObserver since the plugin should now be visible.
   */
  showChPlugin(): void {
    if (this.chPluginObserver) {
      this.chPluginObserver.disconnect();
      this.chPluginObserver = null;
    }
    const chPlugin = this.document.getElementById('ch-plugin');
    if (chPlugin) {
      if (
        this.previousChPluginDisplay !== null &&
        this.previousChPluginDisplay !== undefined
      ) {
        chPlugin.style.setProperty(
          'display',
          this.previousChPluginDisplay,
          'important'
        );
      } else {
        chPlugin.style.removeProperty('display');
      }
    }
  }
}
